import { useState, useRef, useEffect } from 'react'
import { motion, useScroll, useTransform, useSpring } from 'framer-motion'
import { Suspense } from 'react'
import { App } from './App'
import { Overlay } from './Overlay'
import { SplashScreen } from './SplashScreen'
import { create } from 'zustand'
import { Studio } from './Studio'
import { useSectionsStore } from './store'
import { CRTVideoWrapper } from './CRTEffect'

// Define custom font faces for use in the application
const fontStyles = `
@font-face {
  font-family: 'Geomanist-Bold';
  src: url('/fonts/Geomanist-Bold.ttf') format('truetype');
  font-weight: bold;
  font-style: normal;
}
@font-face {
  font-family: 'Inter-UI-Medium';
  src: url('/fonts/Inter-UI-Medium.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
}
`;

// Inject font styles into the document
const useFontStyles = () => {
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = fontStyles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
};

const useVideoStore = create((set, get) => ({
  isPlaying: true,
  setIsPlaying: (playing) => set({ isPlaying: playing }),
  lastProgress: 0,
  setLastProgress: (progress) => set({ lastProgress: progress }),
  scrollDirection: 'down',
  setScrollDirection: (direction) => set({ scrollDirection: direction }),
  togglePlayback: () => set({ isPlaying: !get().isPlaying })
}));

const PHASES = {
  INTRO: 'intro',
  TRANSITION: 'transition',
  GALLERY: 'gallery'
};

const ScrollIndicator = ({ progress, visible }) => {
  const { horizontalProgress } = useSectionsStore();
  
  // Hide scroll indicator when in Studio section (horizontalProgress >= 0.9)
  const shouldShow = visible && horizontalProgress < 0.9;
  
  return (
    <motion.div 
      className="scroll-indicator" 
      initial={{ opacity: 0 }}
      animate={{ opacity: shouldShow ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        bottom: '30px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10, // Lower z-index so it doesn't interfere with Studio
        pointerEvents: 'none'
      }}
    >
      <div className="scroll-icon"></div>
      <div className="scroll-text">Scroll Down</div>
    </motion.div>
  );
};

const PositionedVideo = ({ phase, progress }) => {
  // Get horizontalProgress and direction-aware visibility from the store
  const { getVideoVisibilityThreshold, setVideoScalingUp } = useSectionsStore();
  const { isPlaying } = useVideoStore();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  const finalWidth = 680;
  const finalHeight = 680;
  const sweetSpot = 0.25;

  useEffect(() => {
    if (!containerRef.current) return;
    if (!mounted) {
      setMounted(true);
      return;
    }

    const updatePositionAndOpacity = () => {
      const container = containerRef.current;
      const vProgress = progress.get();

      // Initial full-screen video position
      if (vProgress <= 0.05) {
        container.style.position = 'fixed';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = '100%';
        container.style.height = '100vh';
        container.style.transform = 'none';
        container.style.opacity = '1';
        
        // Video is at full size - scaling up
        setVideoScalingUp(true);
      } 
      // Final video position in gallery - only visible in section 1 (horizontalProgress <= 0.3)
      else if (vProgress >= sweetSpot) {
        container.style.position = 'fixed';
        container.style.top = '50%';
        container.style.left = '40px';
        container.style.width = `${finalWidth}px`;
        container.style.height = `${finalHeight}px`;
        container.style.transform = 'translateY(-50%)';
        
        // Use direction-aware video visibility logic
        const shouldShowVideo = getVideoVisibilityThreshold();
        container.style.opacity = shouldShowVideo ? '1' : '0';
        
        // Video is at small size - not scaling up
        setVideoScalingUp(false);
      } 
      // Transition state - scaling down
      else {
        const t = (vProgress - 0.05) / (sweetSpot - 0.05);
        const width = 100 - (t * (100 - (finalWidth / window.innerWidth * 100)));
        const height = 100 - (t * (100 - (finalHeight / window.innerHeight * 100)));
        const top = t * 50;
        const left = t * 40;
        const translateY = t * -50;

        container.style.position = 'fixed';
        container.style.width = `${width}vw`;
        container.style.height = `${height}vh`;
        container.style.top = `${top}%`;
        container.style.left = `${left}px`;
        container.style.transform = `translateY(${translateY}%)`;
        container.style.opacity = '1';
        
        // Video is transitioning - check if scaling up (going from small to large)
        // If vProgress is decreasing (going back), video is scaling up
        const isScalingUp = vProgress < 0.15; // When transitioning back to full screen
        setVideoScalingUp(isScalingUp);
      }
    };

    updatePositionAndOpacity();
    const positionInterval = setInterval(updatePositionAndOpacity, 10);
    const unsubscribeProgress = progress.onChange(updatePositionAndOpacity);

    return () => {
      clearInterval(positionInterval);
      unsubscribeProgress();
    };
  }, [progress, getVideoVisibilityThreshold, mounted, setVideoScalingUp]);

  // Control video playback based on vertical progress and isPlaying state
  useEffect(() => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    
    // Always ensure video starts muted to prevent browser tab muting
    video.muted = true;
    
    if (isPlaying && progress.get() < 0.4) {
      // Start playing muted first, then handle audio separately if needed
      video.play().catch(err => console.log('Video play error:', err));
    } else {
      video.pause();
    }
  }, [isPlaying, progress]);

  return (
    <div
      ref={containerRef}
      className="positioned-video"
      style={{
        position: 'fixed',
        zIndex: 1500, // Above section titles (1000/900) but below Studio (2000) and overlay (10000)
        transition: 'opacity 0.5s ease-in-out', // Add smooth fade transition
      }}
    >
      <CRTVideoWrapper enabled={true} intensity={1}>
        <video
          ref={videoRef}
          className="transition-video"
          src="/videos/gns-intro-web.mp4"
          autoPlay
          loop
          muted
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: phase !== PHASES.INTRO ? '12px' : '0',
            boxShadow: phase !== PHASES.INTRO ? '0 10px 30px rgba(0,0,0,0.5)' : 'none',
          }}
        />
      </CRTVideoWrapper>
    </div>
  );
};

export const TransitionLayout = () => {
  const [currentPhase, setCurrentPhase] = useState(PHASES.INTRO);
  const [appLoaded, setAppLoaded] = useState(false);
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const smoothScrollYProgress = useSpring(scrollYProgress, { damping: 20, stiffness: 100 });

  // Force scroll to top on component mount to ensure splash screen shows
  useEffect(() => {
    // Reset scroll position to top on page load/refresh
    window.scrollTo(0, 0);
    
    // Also reset any stored scroll position
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    
    // Reset all store states to initial values
    const { resetToInitialState } = useSectionsStore.getState();
    resetToInitialState();
    
    // Force initial phase to INTRO
    setCurrentPhase(PHASES.INTRO);
    setAppLoaded(false);
  }, []);

  useEffect(() => {
    const { setProgress } = useSectionsStore.getState();
    const unsubscribe = smoothScrollYProgress.onChange(progress => {
      setProgress(progress);
    });
    return () => unsubscribe();
  }, [smoothScrollYProgress]);

  useFontStyles();

  const appOpacity = useTransform(smoothScrollYProgress, [0.1, 0.25], [0, 1]);

  useEffect(() => {
    const updatePhase = (progress) => {
      let newPhase;
      if (progress < 0.05) {
        newPhase = PHASES.INTRO;
      } else if (progress < 0.25) {
        newPhase = PHASES.TRANSITION;
      } else {
        newPhase = PHASES.GALLERY;
        if (!appLoaded) setAppLoaded(true);
      }
      
      if (newPhase !== currentPhase) {
        setCurrentPhase(newPhase);
      }
    };
    const unsubscribe = smoothScrollYProgress.onChange(updatePhase);
    return () => unsubscribe();
  }, [smoothScrollYProgress, appLoaded, currentPhase]);

  return (
    <div 
      ref={containerRef}
      className="transition-container"
      style={{ 
        height: '500vh',
        width: '100%',
        position: 'relative',
        overflowX: 'hidden'
      }}
    >
      <Overlay />
      <PositionedVideo phase={currentPhase} progress={smoothScrollYProgress} />
      <ScrollIndicator 
        progress={smoothScrollYProgress} 
        visible={currentPhase === PHASES.INTRO}
      />
      <motion.div
        className="splash-title-overlay"
        initial={{ opacity: 1 }}
        animate={{ opacity: currentPhase === PHASES.INTRO ? 1 : 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          zIndex: 1600, // Above video (1500) but below overlay (10000)
          pointerEvents: 'none'
        }}
      >
        <h1 style={{ color: 'white', fontSize: 'min(10vw, 120px)', fontWeight: '800', textShadow: '0 0 10px rgba(100, 255, 218, 0.5)', margin: 0, letterSpacing: '1px',lineHeight: '1' }}
        >Glitch Candies Studio</h1>
      </motion.div>
      <Studio />
      <motion.div
        className="app-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          opacity: appOpacity,
          zIndex: 500, // Lowered from 2 to 500 to be below video (1500)
          pointerEvents: currentPhase === PHASES.GALLERY ? 'auto' : 'none'
        }}
      >
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </motion.div>
    </div>
  );
};